import { Settings, Users, Volume2, Eye, EyeOff } from 'lucide-react';
import type { VoiceFormData } from '../VoiceChannelSettings';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { sanitizePhoneInput } from '@/lib/phone';

interface TwilioProviderFormProps {
  formData: VoiceFormData;
  onChange: (field: keyof VoiceFormData, value: unknown) => void;
  showPasswords: Record<string, boolean>;
  onTogglePassword: (field: string) => void;
}

const TTS_VOICES = [
  { value: 'Polly.Joanna', label: 'Joanna (US English, Female)' },
  { value: 'Polly.Matthew', label: 'Matthew (US English, Male)' },
  { value: 'Polly.Amy', label: 'Amy (British English, Female)' },
  { value: 'Polly.Brian', label: 'Brian (British English, Male)' },
  { value: 'Polly.Emma', label: 'Emma (British English, Female)' },
];

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200';

/**
 * Twilio provider configuration.
 *
 * Lifted unchanged out of VoiceChannelSettings when Exotel arrived and the
 * single form became a set of provider tabs. The fields, placeholders and
 * behaviour are exactly what they were — this is a move, not a redesign, so a
 * tenant configuring Twilio sees the same screen as before.
 */
export default function TwilioProviderForm({
  formData,
  onChange,
  showPasswords,
  onTogglePassword,
}: TwilioProviderFormProps) {
  return (
    <div className="space-y-6">
      {/* Twilio Credentials */}
      <div>
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Twilio Credentials
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Account SID *</label>
            <input
              type="text"
              value={formData.twilioAccountSid}
              onChange={(e) => onChange('twilioAccountSid', e.target.value)}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Auth Token *</label>
            <div className="relative">
              <input
                type={showPasswords.authToken ? 'text' : 'password'}
                value={formData.twilioAuthToken}
                onChange={(e) => onChange('twilioAuthToken', e.target.value)}
                placeholder="Enter auth token"
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => onTogglePassword('authToken')}
                aria-label={showPasswords.authToken ? 'Hide auth token' : 'Show auth token'}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
              >
                {showPasswords.authToken ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Phone Number *</label>
            <input
              type="text"
              value={formData.twilioPhoneNumber}
              // E.164 has no separators, so only digits and the leading + get through.
              onChange={(e) => onChange('twilioPhoneNumber', sanitizePhoneInput(e.target.value).replace(/[\s().-]/g, ''))}
              inputMode="tel"
              placeholder="+1234567890"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Twilio Flex (Optional) */}
      <div>
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Users className="h-4 w-4" />
          Twilio Flex (Agent Handoff)
        </h3>
        <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
          Configure Flex for seamless handoff from AI to human agents.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Workspace SID</label>
            <input
              type="text"
              value={formData.flexWorkspaceSid}
              onChange={(e) => onChange('flexWorkspaceSid', e.target.value)}
              placeholder="WSxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Workflow SID</label>
            <input
              type="text"
              value={formData.flexWorkflowSid}
              onChange={(e) => onChange('flexWorkflowSid', e.target.value)}
              placeholder="WWxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Queue SID</label>
            <input
              type="text"
              value={formData.flexQueueSid}
              onChange={(e) => onChange('flexQueueSid', e.target.value)}
              placeholder="WQxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* TTS Settings */}
      <div>
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Volume2 className="h-4 w-4" />
          Text-to-Speech Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>TTS Provider</label>
            <ThemedSelect
              value={formData.ttsProvider}
              onChange={(e) => onChange('ttsProvider', e.target.value)}
              className={inputClass}
            >
              <option value="twilio">Twilio (Amazon Polly)</option>
              <option value="elevenlabs">ElevenLabs</option>
            </ThemedSelect>
          </div>
          {formData.ttsProvider === 'twilio' ? (
            <div>
              <label className={labelClass}>Voice</label>
              <ThemedSelect
                value={formData.twilioVoice}
                onChange={(e) => onChange('twilioVoice', e.target.value)}
                className={inputClass}
              >
                {TTS_VOICES.map((voice) => (
                  <option key={voice.value} value={voice.value}>
                    {voice.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          ) : (
            <>
              <div>
                <label className={labelClass}>ElevenLabs API Key</label>
                <div className="relative">
                  <input
                    type={showPasswords.elevenLabs ? 'text' : 'password'}
                    value={formData.elevenLabsApiKey}
                    onChange={(e) => onChange('elevenLabsApiKey', e.target.value)}
                    placeholder="Enter ElevenLabs API key"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => onTogglePassword('elevenLabs')}
                    aria-label={
                      showPasswords.elevenLabs ? 'Hide ElevenLabs key' : 'Show ElevenLabs key'
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                  >
                    {showPasswords.elevenLabs ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>ElevenLabs Voice ID</label>
                <input
                  type="text"
                  value={formData.elevenLabsVoiceId}
                  onChange={(e) => onChange('elevenLabsVoiceId', e.target.value)}
                  placeholder="Voice ID"
                  className={inputClass}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
